/**
 * Ad Initializer - Generic initialization orchestration for ad serving
 *
 * This module provides identity resolution, initialization guards, and
 * runtime management that is shared across Narcity and MTL Blog ad modules.
 */

import { logInfo, logWarn, logError } from './lib/logger';
import { detectDevice } from './lib/deviceDetection';
import { loadScript } from './lib/scriptLoader';
import { getBrandConfig } from './brandConfigs.js';
import { isDevOrSandboxHost } from './lib/cdmAdtest';
import { resolveAuctionStrategy } from './lib/adRuntime';
const identity = require('./lib/identity');
const { configureSdkDiagnostics, reportSdkDiagnostic } = require('./lib/sdkDiagnostics');
const { buildUserIds } = require('./lib/adIdentityConfig');
const { inAdHoldout } = require('./lib/adExperiments');
const { isNotFoundPage, isStaticPage } = require('./lib/insights/pageContext');
const { ensureStudyAssignments, clearStudyAssignments } = require('./lib/insights/study');

export { isDevOrSandboxHost };

const PREBID_ASSET_PATH = '/prebid/prebid10.25.0-gpt-config.js';
const DEFAULT_PREBID_URL = `https://auth.getmilla.com${PREBID_ASSET_PATH}`;

function resolveDefaultPrebidUrl() {
  const runtimeDocument = typeof document !== 'undefined' ? document : null;
  const scripts = runtimeDocument && runtimeDocument.scripts
    ? Array.from(runtimeDocument.scripts)
    : [];
  const sdkScript = scripts
    .slice()
    .reverse()
    .find((script) =>
      /\/milla-auth(?:\.min)?\.js(?:[?#]|$)/i.test(String(script && script.src))
    );

  if (!sdkScript || !sdkScript.src) {
    return DEFAULT_PREBID_URL;
  }

  try {
    return new URL(PREBID_ASSET_PATH, sdkScript.src).href;
  } catch (_error) {
    return DEFAULT_PREBID_URL;
  }
}

function resolvePrebidUrl(userConfig = {}) {
  return typeof userConfig.prebidUrl === 'string'
    ? userConfig.prebidUrl
    : resolveDefaultPrebidUrl();
}

/**
 * Resolve the current user's identity from the MillaAuth SDK for:
 * - Pushing email into UID2 userIds
 * - Detecting Pro status to control ad serving
 *
 * This intentionally never throws; failures result in null email
 * and non-pro status so ads can still initialize without UID2.
 *
 * OPTIMIZATION: If user is known to be logged out, skip API calls entirely
 * to reduce failed requests and speed up ad initialization.
 *
 * @param {string} brand - Brand name for logging ('narcity' or 'mtlblog')
 * @returns {Promise<{email: string|null, isPro: boolean}>}
 */
export async function resolveMillaIdentityForAds(brand = 'narcity') {
  let email = null;
  let isPro = false;

  if (typeof window === 'undefined') {
    return { email, isPro };
  }

  const auth = window.MillaAuth;
  if (!auth || typeof auth.fetch !== 'function') {
    logWarn('MillaAuth.fetch not available; proceeding without UID2 identity.');
    return { email, isPro };
  }

  // OPTIMIZATION: If we've confirmed user is logged out, skip API calls entirely
  if (typeof auth.isKnownLoggedOut === 'function' && auth.isKnownLoggedOut()) {
    logInfo('User is known to be logged out; skipping identity fetch for ads.');
    return { email, isPro };
  }

  let base = '';
  let timeoutMs = 2500;
  try {
    const cfg = typeof auth.getConfig === 'function' ? auth.getConfig() : {};
    base = (cfg && cfg.issuer) || window.location.origin;
    if (cfg && Number(cfg.adsAuthTimeoutMs) > 0) timeoutMs = Number(cfg.adsAuthTimeoutMs);
  } catch (err) {
    logWarn(
      `Unable to determine Milla issuer for ${brand} ads; falling back to window.location.origin.`,
      err
    );
    base = window.location.origin;
  }

  const normalizedBase = base ? base.replace(/\/$/, '') : '';
  const profileUrl = `${normalizedBase}/me`;

  try {
    // Every display factory waits on this. A refused or slow auth frame must
    // not hold the page's ads: start without a profile after the deadline.
    let deadline;
    const response = await Promise.race([
      auth.fetch(profileUrl, { credentials: 'include' }),
      new Promise((resolve) => { deadline = setTimeout(() => resolve(null), timeoutMs); })
    ]).finally(() => clearTimeout(deadline));
    if (response === null) {
      logWarn(`Milla auth did not answer within ${timeoutMs} ms; starting ${brand} ads without a profile.`);
      reportSdkDiagnostic('ads_auth_deadline');
      // Display ads start as for a signed-out reader. `unresolved` lets
      // Stories keep its rule: an unknown reader is not assumed to be free.
      return { email, isPro, unresolved: true };
    }
    if (response && response.ok) {
      const data = await response.json().catch(() => ({}));
      if (data && typeof data === 'object') {
        if (data.email && typeof data.email === 'string') {
          email = data.email;
        }
        // proStatus is already included in the profile via Stripe webhook sync
        const rawProStatus = data.proStatus;
        if (
          typeof rawProStatus === 'string' &&
          rawProStatus.toLowerCase() === 'active'
        ) {
          isPro = true;
        }
      }
    }
  } catch (err) {
    logWarn(
      `Failed to fetch Milla profile for ${brand} ads; continuing without email.`,
      err
    );
  }

  return { email, isPro };
}

/**
 * Build a unique initialization key based on page state
 * Used to detect navigation in SPAs and trigger re-initialization
 *
 * @param {object} userConfig - User configuration
 * @param {string} pageType - Page type ('article', 'homepage', etc.)
 * @returns {string} Unique initialization key
 */
export function buildInitKey(userConfig, pageType, device) {
  const deviceKey = typeof device === 'string' && device.trim() ? device.trim() : 'auto';
  if (userConfig && typeof userConfig.pageKey === 'string' && userConfig.pageKey.trim()) {
    return `${userConfig.pageKey.trim()}|${deviceKey}`;
  }
  if (typeof window === 'undefined') {
    return `${pageType || 'article'}|${deviceKey}`;
  }
  const loc = window.location;
  const path = loc ? `${loc.pathname || ''}${loc.search || ''}${loc.hash || ''}` : '';
  return `${pageType || 'article'}|${deviceKey}|${path}`;
}

/**
 * Reset the ad runtime state before re-initialization
 * Clears Prebid ad units and GPT slots
 */
export function resetAdRuntime() {
  if (typeof window === 'undefined') return;

  if (!window.adData || typeof window.adData !== 'object') {
    window.adData = { placedAdCount: 1, trendingAdsInserted: 0 };
  } else {
    window.adData.placedAdCount = 1;
    window.adData.trendingAdsInserted = 0;
  }

  if (window._millaAdRuntime && typeof window._millaAdRuntime.destroy === 'function') {
    try {
      window._millaAdRuntime.destroy();
    } catch (err) {
      logWarn('Failed to destroy the active ad auction runtime before re-init', err);
    }
  }

  const pbjs = window.pbjs;
  try {
    if (pbjs && typeof pbjs.removeAdUnit === 'function') {
      const codes = typeof pbjs.getAdUnitCodes === 'function'
        ? pbjs.getAdUnitCodes()
        : Array.isArray(pbjs.adUnits)
          ? pbjs.adUnits.map((unit) => unit && unit.code).filter(Boolean)
          : [];
      if (codes.length > 0) {
        pbjs.removeAdUnit(codes);
      }
    }
  } catch (err) {
    logWarn('Failed to clear Prebid ad units before re-init', err);
  }

  const googletag = window.googletag;
  if (googletag && googletag.cmd && typeof googletag.cmd.push === 'function') {
    googletag.cmd.push(function () {
      try {
        const pubads = googletag.pubads && googletag.pubads();
        if (pubads && pubads._millaAutoRefreshInstalled) {
          if (
            pubads._millaAutoRefreshController
            && typeof pubads._millaAutoRefreshController.destroy === 'function'
          ) {
            pubads._millaAutoRefreshController.destroy();
          }
          pubads._millaAutoRefreshInstalled = false;
        }
        if (typeof googletag.destroySlots === 'function') {
          googletag.destroySlots();
        }
      } catch (err) {
        logWarn('Failed to reset GPT slots before re-init', err);
      }
    });
  }
}

/**
 * Creates an initialization manager for tracking init state
 * @returns {object} Init manager with state and helper methods
 */
export function createInitManager() {
  let initialized = false;
  let lastInitKey = null;

  return {
    isInitialized: () => initialized,
    getLastInitKey: () => lastInitKey,

    /**
     * Check if re-initialization should occur
     * @param {object} userConfig - User configuration
     * @param {string} pageType - Page type
     * @param {string} brand - Brand name for logging
     * @returns {boolean} Whether to proceed with initialization
     */
    shouldInit(userConfig, pageType, brand, device) {
      if (!initialized) {
        return true;
      }

      const initKey = buildInitKey(userConfig, pageType, device);
      const forceReinit = userConfig.forceReinit === true;
      const explicitDeviceOverride =
        typeof userConfig.device === 'string' && userConfig.device.trim().length > 0;
      const allowReinit = userConfig.allowReinit === true || forceReinit || explicitDeviceOverride;
      const shouldReinit = allowReinit && (forceReinit || initKey !== lastInitKey);

      if (!shouldReinit) {
        logInfo(`${brand} ads already initialized; skipping.`);
        return false;
      }

      logInfo(`Re-initializing ${brand} ads for new page.`, {
        previous: lastInitKey,
        next: initKey
      });
      resetAdRuntime();
      initialized = false;
      return true;
    },

    /**
     * Mark initialization as complete
     * @param {object} userConfig - User configuration
     * @param {string} pageType - Page type
     */
    markInitialized(userConfig, pageType, device) {
      initialized = true;
      lastInitKey = buildInitKey(userConfig, pageType, device);
    },

    /**
     * Reset initialization state
     */
    reset() {
      initialized = false;
      lastInitKey = null;
    }
  };
}

let snippetGlobalsWarned = false;

/**
 * Advertising identity for this page view (plans/ads-identity-unification.md,
 * owner decision 2026-09-09):
 *  - `window.ppid` = the derived, consent-gated ads id from the identity
 *    module (member-derived when signed in, visitor-derived otherwise), or
 *    null — there is no fallback to the Prebid sharedId or to an email hash;
 *  - Prebid `userIds` come from the SDK's own config (lib/adIdentityConfig.js)
 *    and only when the advertising decision is APPROVED; a page-level
 *    `userConfig.userIds` still wins when a page passes one explicitly;
 *  - the RebelMouse snippet globals `window.userIds` / `window.customHash`
 *    are ignored (one info line) — the CMS snippet is no longer load-bearing;
 *  - browsers in the `ppid` experiment holdout (adExperimentsConfig.js) get
 *    no PPID; their Prebid `userIds` are unchanged.
 * @param {{ email?: string|null, ads?: object|null, explicitUserIds?: Array|null }} input
 * @returns {Array} the Prebid userIds to configure (possibly empty)
 */
export function setupUserIdentity(input = {}) {
  const email = typeof input.email === 'string' && input.email ? input.email : null;
  const ads = input.ads && typeof input.ads === 'object' ? input.ads : null;
  const adsConsent = ads && ads.state === 'APPROVED' ? 'APPROVED' : (ads && ads.state) || 'UNDECIDED';
  const ppid = adsConsent === 'APPROVED' && ads && typeof ads.id === 'string' && /^[a-f0-9]{32}$/.test(ads.id)
    && !inAdHoldout('ppid') ? ads.id : null;
  const userIds = buildUserIds({ adsConsent, email, explicit: Array.isArray(input.explicitUserIds) ? input.explicitUserIds : null });

  if (typeof window !== 'undefined') {
    if (!snippetGlobalsWarned && (typeof window.customHash === 'function' || (Array.isArray(window.userIds) && !window.__millaUserIdsOwned))) {
      snippetGlobalsWarned = true;
      logInfo('Page-level window.userIds / window.customHash are ignored since 2026-09-09; the SDK owns Prebid user ids and the GAM PPID.');
    }
    window.ppid = ppid;
    window.userIds = userIds;
    window.__millaUserIdsOwned = true;
  }

  return userIds;
}

/**
 * The advertising block for this page view. Never rejects: any failure means
 * "no ads identity" and the ad stack proceeds without a PPID or id modules.
 */
async function resolveAdsIdentityForAds() {
  try {
    return await identity.getAdsIdentity();
  } catch (err) {
    logWarn('Ads identity unavailable; continuing without a PPID.', err);
    return { state: 'UNDECIDED', id: null, source: null, policyVersion: null, reason: 'ads_identity_error' };
  }
}

/**
 * The brand lift study assignment (lib/insights/study.js), never waited for:
 * a stored one applies at once, and a first one joins the page's GAM
 * targeting when it arrives (the campaign's line items need the key-value, so
 * requests sent before then can't serve it). Never rejects: a failure only
 * means no study key-value on this page.
 */
function resolveStudyAssignmentsForAds(brand) {
  try {
    return ensureStudyAssignments(brand).catch(() => null);
  } catch (_) {
    return Promise.resolve(null);
  }
}

// A reader whose advertising decision is DENIED is out of the study: no
// bucket key-value, no exposure counters.
function applyStudyConsent(brand, ads) {
  if (!ads || ads.state !== 'DENIED') return;
  try {
    clearStudyAssignments(brand);
  } catch (_) {
    // the study never blocks ad init
  }
}

/**
 * Build the context object for ad initialization
 * @param {object} options - Configuration options
 * @returns {object} Context object for ad modules
 */
export function buildAdContext(options) {
  const {
    brand,
    userConfig = {},
    email,
    isPro,
    ignoreProStatus = false,
    userIds,
    adsConsent = 'UNDECIDED',
    emitter,
    pageType,
    device,
    walled = false
  } = options;

  const config = typeof brand === 'string' ? getBrandConfig(brand) : brand;

  const prebidUrl = resolvePrebidUrl(userConfig);
  const gptUrl =
    typeof userConfig.gptUrl === 'string'
      ? userConfig.gptUrl
      : 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

  const pro = ignoreProStatus ? null : isPro ? 'active' : null;
  const isLoggedIn = !!email || !!isPro;

  // Get values from userConfig or window
  const narcityGlobalState = userConfig.narcityGlobalState || window.narcityGlobalState;
  // 404 pages get the No Ads treatment: Google allows no ads on error pages.
  const notFoundPage = isNotFoundPage({
    bootstrap: userConfig.rebelmouseBootstrap !== undefined
      ? userConfig.rebelmouseBootstrap
      : window.REBELMOUSE_BOOTSTRAP_DATA
  });
  const _noads = notFoundPage
    ? 'yes'
    : (userConfig._noads !== undefined ? userConfig._noads : window._noads);
  const _urlonly = userConfig._urlonly !== undefined ? userConfig._urlonly : window._urlonly;
  const _sponsored = userConfig._sponsored !== undefined ? userConfig._sponsored : window._sponsored;
  const _fr_lang = userConfig._fr_lang !== undefined ? userConfig._fr_lang : window._fr_lang;
  const fwjson = userConfig.fwjson || window.fwjson;
  const _googCsa = userConfig._googCsa || window._googCsa;
  const staticPage = isStaticPage({
    staticPagesSectionId: config.staticPagesSectionId,
    post_sections: userConfig.post_sections !== undefined ? userConfig.post_sections : window.post_sections
  });
  const insights = userConfig.insights || window.narcityInsightsConfig || null;
  const auctionStrategy = resolveAuctionStrategy({
    brand: config.brand,
    userConfig,
    runtimeWindow: window
  });
  const adDiagnosticsEnabled = userConfig.adDiagnostics === true
    || window._millaAdsDiagnosticsEnabled === true;
  const pageKey = buildInitKey(userConfig, pageType, device);

  const pbjs = (window.pbjs = window.pbjs || {});
  pbjs.que = pbjs.que || [];

  const googletag = (window.googletag = window.googletag || {});
  googletag.cmd = googletag.cmd || [];

  return {
    prebidUrl,
    gptUrl,
    narcityGlobalState,
    _noads,
    _urlonly,
    _sponsored,
    _fr_lang,
    staticPage,
    fwjson,
    userIds,
    adsConsent,
    _googCsa,
    insights,
    pbjs,
    googletag,
    pro,
    emitter,
    isLoggedIn,
    pageType,
    device,
    walled: walled === true,
    auctionStrategy,
    adDiagnosticsEnabled,
    pageKey,
    brand: config.brand,
    config
  };
}

/**
 * Preload ad scripts without initializing
 * @param {object} userConfig - Configuration with optional prebidUrl and gptUrl
 */
// GPT serves the library and the ad requests; the bid endpoints are the
// vendored Prebid build's adapters (portal/public/prebid). Open the GPT
// connection and resolve bidder hosts while the scripts download.
const AD_PRECONNECT_ORIGINS = ['https://securepubads.g.doubleclick.net'];
const BIDDER_DNS_PREFETCH_ORIGINS = [
  'https://ib.adnxs.com',
  'https://htlb.casalemedia.com',
  'https://fastlane.rubiconproject.com',
  'https://a.teads.tv',
  'https://direct.adsrvr.org',
  'https://c2shb.pubgw.yahoo.com'
];

function addAdResourceHints() {
  const head = document.head || document.getElementsByTagName('head')[0];
  if (!head || document.querySelector('link[data-milla-ad-hint]')) return;
  const add = (rel, href) => {
    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    link.setAttribute('data-milla-ad-hint', '');
    head.appendChild(link);
  };
  AD_PRECONNECT_ORIGINS.forEach((origin) => add('preconnect', origin));
  BIDDER_DNS_PREFETCH_ORIGINS.forEach((origin) => add('dns-prefetch', origin));
}

export function preloadAdScripts(userConfig = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  addAdResourceHints();
  const prebidUrl = resolvePrebidUrl(userConfig);
  const gptUrl =
    typeof userConfig.gptUrl === 'string'
      ? userConfig.gptUrl
      : 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

  loadScript(prebidUrl).catch((err) =>
    logWarn('Ad preload: failed to load Prebid script', err)
  );
  loadScript(gptUrl).catch((err) =>
    logWarn('Ad preload: failed to load GPT script', err)
  );
}

/**
 * Initialize the global ad data state
 */
export function initAdDataState() {
  if (typeof window === 'undefined') return;

  if (!window.auction) {
    window.auction = {};
  }
  if (!window.adData || typeof window.adData.placedAdCount !== 'number') {
    window.adData = {
      placedAdCount: 1,
      trendingAdsInserted: 0
    };
  }
}

/**
 * Creates a generic ad initialization function for a brand
 * @param {string} brand - Brand name ('narcity' or 'mtlblog')
 * @param {object} deviceHandlers - Object with desktop and mobile init functions
 * @param {object} [options] - Optional configuration for logging
 * @param {string} [options.logLabel] - Label used in log messages
 * @returns {Function} Initialization function
 */
export function createBrandAdInitializer(brand, deviceHandlers, options = {}) {
  const initManager = createInitManager();
  const brandConfig = getBrandConfig(brand);
  const logLabel = options.logLabel || brand;

  return function initAds(userConfig = {}, emitter) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const device = userConfig.device || detectDevice();
    const pageType = userConfig.pageType || 'article';

    if (!initManager.shouldInit(userConfig, pageType, brand, device)) {
      return;
    }

    initManager.markInitialized(userConfig, pageType, device);
    initAdDataState();
    configureSdkDiagnostics({ device, pageType });

    if (emitter && typeof emitter.emit === 'function') {
      emitter.emit(`ads:${brand}:init`);
    }

    const ignoreProStatus =
      userConfig.ignoreProStatus === true ||
      (typeof window !== 'undefined' && window._millaIgnoreProStatus === true);

    // Page-level `userConfig.userIds` is an explicit override; the RebelMouse
    // `window.userIds` global is no longer read (2026-09-09).
    const explicitUserIds = Array.isArray(userConfig.userIds) ? userConfig.userIds : null;
    let userIds = [];
    // A walled article view never requests the units the login wall hides
    // (plans/dynamic-login-wall.md); the verdict never rejects.
    const walledView = Promise.resolve(pageType === 'article' && options.loginWallVerdict && options.loginWallVerdict());

    Promise.all([
      resolveMillaIdentityForAds(brand),
      resolveAdsIdentityForAds(),
      resolveStudyAssignmentsForAds(brand),
      walledView
    ])
      .then(([{ email, isPro }, ads, , walled]) => {
        applyStudyConsent(brand, ads);
        userIds = setupUserIdentity({ email, ads, explicitUserIds });

        const ctx = buildAdContext({
          brand: brandConfig,
          userConfig,
          email,
          isPro,
          ignoreProStatus,
          userIds,
          adsConsent: ads && ads.state ? ads.state : 'UNDECIDED',
          emitter,
          pageType,
          device,
          walled
        });

        dispatchToHandler(ctx, device, pageType, deviceHandlers, emitter, brand);
      })
      .catch((err) => {
        logError(`Failed to resolve identity for ${logLabel} ads`, err);
        reportSdkDiagnostic('ads_error');
        if (emitter && typeof emitter.emit === 'function') {
          emitter.emit(`ads:${brand}:error`, err);
        }
        userIds = setupUserIdentity({ email: null, ads: null, explicitUserIds });

        return walledView.then((walled) => {
          const ctx = buildAdContext({
            brand: brandConfig,
            userConfig,
            email: null,
            isPro: false,
            ignoreProStatus,
            userIds,
            adsConsent: 'UNDECIDED',
            emitter,
            pageType,
            device,
            walled
          });

          dispatchToHandler(ctx, device, pageType, deviceHandlers, emitter, brand);
        });
      });
  };
}

/**
 * Dispatch to appropriate handler based on device and page type
 */
function dispatchToHandler(ctx, device, pageType, handlers, emitter, brand) {
  const isTablet = device === 'tablet';
  const isMobile = device === 'mobile';

  logInfo(`[dispatch] device=${device}, pageType=${pageType}, brand=${brand}, hasTabletHandler=${!!handlers.articleTablet}`);

  if (pageType === 'article') {
    if (isTablet && handlers.articleTablet) {
      logInfo('[dispatch] → routing to articleTablet handler');
      handlers.articleTablet(ctx);
    } else if ((isMobile || isTablet) && handlers.articleMobile) {
      logInfo(`[dispatch] → routing to articleMobile handler (isTablet=${isTablet}, isMobile=${isMobile})`);
      handlers.articleMobile(ctx);
    } else if (handlers.articleDesktop) {
      logInfo('[dispatch] → routing to articleDesktop handler');
      handlers.articleDesktop(ctx);
    }
  } else if (pageType === 'homepage' || pageType === 'section') {
    if ((isMobile || isTablet) && handlers.homepageMobile) {
      handlers.homepageMobile(ctx);
    } else if (handlers.homepageDesktop) {
      handlers.homepageDesktop(ctx);
    }
  } else if (emitter && typeof emitter.emit === 'function') {
    emitter.emit(`ads:${brand}:ready`);
  }
}

export async function resolveStoriesAdContext(brand, userConfig = {}) {
  let deadline;
  let profile; let ads;
  try {
    [profile, ads] = await Promise.all([
      Promise.race([resolveMillaIdentityForAds(brand), new Promise((resolve) => { deadline = setTimeout(() => resolve(null), 3000); })]),
      resolveAdsIdentityForAds(),
      resolveStudyAssignmentsForAds(brand)
    ]);
  } finally { clearTimeout(deadline); }
  applyStudyConsent(brand, ads);
  const { email = null, isPro = false } = profile || {};
  const userIds = setupUserIdentity({ email, ads, explicitUserIds: userConfig.userIds });
  const ctx = buildAdContext({ brand, userConfig, email, isPro, userIds, adsConsent: ads.state, pageType: 'shows', device: detectDevice() });
  // A profile lookup that hit the display-ads deadline is still unknown here.
  ctx.identityResolved = Boolean(profile) && !profile.unresolved;
  if (ctx.identityResolved && ctx.pro !== 'active' && ![ctx._noads, ctx._urlonly, ctx._sponsored].includes('yes') && userConfig.adsEnabled !== false) {
    try {
      // A publisher may already have initialized Prebid for its display slots.
      if (typeof ctx.pbjs.requestBids !== 'function') {
        let deadline;
        try {
          await Promise.race([loadScript(ctx.prebidUrl), new Promise((_, reject) => { deadline = setTimeout(() => reject(new Error('Prebid load timed out')), 3000); })]);
        } finally { clearTimeout(deadline); }
        const { configurePrebid } = require('./lib/prebidConfig');
        if (typeof ctx.pbjs.setConfig === 'function') configurePrebid(ctx.pbjs, userIds);
      }
    } catch (error) { logWarn('Stories Prebid unavailable; continuing with GAM.', error); }
  }
  return ctx;
}
